import { useEffect, useRef, useState } from 'react';
import { EASE } from '../../styles/theme';
import { useTheme, useUI } from '../../context/ThemeContext';

// ── PROGRESS BAR (gradient, animiert) ────────────────────────────────────────
function SheetProgress({ step, total }) {
  const { T } = useTheme();
  const pct = total > 1 ? (step / (total - 1)) * 100 : 100;
  return (
    <div style={{
      height: 3, borderRadius: 999, background: T.line || T.glassBorder,
      overflow: 'hidden', margin: '0 16px 14px',
    }}>
      <div style={{
        height: '100%', width: `${pct}%`,
        background: '#E4544A',
        transition: `width 380ms ${EASE}`,
      }} />
    </div>
  );
}

// Runder Chrome-Button (Back/Close) — subtile, organische Motion: sanfter
// BG-Fill statt Border-Snap + weiches Press-Squish (wie BrandModal).
function SheetCircleBtn({ T, onClick, label, path, spin = false }) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      className={spin ? 'mo-close' : undefined}
      onMouseEnter={e => { e.currentTarget.style.background = T.itemHover; e.currentTarget.style.color = T.text; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = T.sub; e.currentTarget.style.transform = 'scale(1)'; }}
      onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.88)'; }}
      onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
      style={{
        width: 34, height: 34, borderRadius: 999,
        background: 'transparent', border: `1px solid ${T.line}`, color: T.sub,
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        WebkitTapHighlightColor: 'transparent',
        transition: 'background 200ms ease, color 200ms ease, transform 200ms cubic-bezier(.34,1.5,.5,1)',
      }}
    >
      <svg className={spin ? 'mo-close-ico' : undefined} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d={path} />
      </svg>
    </button>
  );
}

// ── BOTTOM SHEET (slide-up, drag-to-close) ───────────────────────────────────
//
// Props:
//   open       — bool, steuert Sichtbarkeit
//   onClose    — () => void, Backdrop-Click / Drag-down / X
//   title      — String, optional (sticky-Header)
//   step       — Number, aktuelle Step-Position (0-basiert)
//   totalSteps — Number, Gesamtanzahl (für Progress)
//   onBack     — () => void | null, optional (Pfeil links neben Title)
//   children   — Step-Content
export default function WizardSheet({ open, onClose, step = 0, totalSteps = 1, onBack, children }) {
  const { T } = useTheme();
  const ui = useUI();
  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(false);
  const [drag, setDrag] = useState(0);
  const dragStart = useRef(null); // { x, y }
  const allowDrag = useRef(false);
  const axisRef = useRef(null);   // 'h' (Karten wischen) | 'v' (Sheet ziehen) | null
  const contentRef = useRef(null);

  // Mount/Unmount + slide-Animation
  useEffect(() => {
    if (open) {
      setMounted(true);
      // doppel-rAF, damit transition feuert
      requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
    } else if (mounted) {
      setVisible(false);
      const t = setTimeout(() => setMounted(false), 320);
      return () => clearTimeout(t);
    }
  }, [open, mounted]);

  // ESC zum Schließen
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // body-scroll-lock
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!mounted) return null;

  // ── Drag-to-close auf der GANZEN Sheet-Fläche ─────────────────────────────
  // Wisch nach unten schließt — sofern der Inhalt ganz oben steht (sonst scrollt
  // der Inhalt zuerst, wie bei iOS-Sheets). Greift überall, nicht nur am Greifer.
  //
  // WICHTIG: Es reicht NICHT, nur den äußeren `contentRef` zu prüfen. Steps wie
  // die Coin-Liste haben einen EIGENEN scrollbaren Container im Inhalt — dann
  // bleibt der äußere ScrollTop bei 0 und ein Wisch in der Liste schloss fälsch-
  // licherweise das ganze Sheet. Deshalb suchen wir den nächsten scrollbaren
  // Vorfahren UNTER dem Finger und erlauben den Drag nur, wenn DIESER ganz oben
  // steht (echtes iOS-Sheet-Verhalten: erst scrollen, am Anschlag dann schließen).
  const scrollableUnder = (node, root) => {
    let el = node;
    while (el && el !== root && el instanceof Element) {
      const oy = window.getComputedStyle(el).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight + 1) return el;
      el = el.parentElement;
    }
    return contentRef.current || null;
  };
  const onTouchStart = (e) => {
    const t = e.touches[0];
    dragStart.current = { x: t.clientX, y: t.clientY };
    const scroller = scrollableUnder(e.target, e.currentTarget);
    allowDrag.current = !scroller || scroller.scrollTop <= 0;
    axisRef.current = null;
    setDrag(0);
  };
  const onTouchMove = (e) => {
    if (dragStart.current == null) return;
    const t = e.touches[0];
    const dx = t.clientX - dragStart.current.x;
    const dy = t.clientY - dragStart.current.y;
    // Achse beim ersten klaren Move festlegen und SPERREN: horizontal = Karten/
    // Listen wischen (kein Schließen), vertikal = Sheet ziehen. Verhindert, dass
    // ein leichtes Vertikal-Wackeln beim Karten-Wischen das Sheet runterzieht.
    if (axisRef.current == null) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      axisRef.current = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
    }
    if (axisRef.current !== 'v' || !allowDrag.current) return;
    if (dy > 0) setDrag(dy);
  };
  const onTouchEnd = () => {
    if (drag > 110) onClose?.();
    dragStart.current = null;
    allowDrag.current = false;
    axisRef.current = null;
    setDrag(0);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      pointerEvents: visible ? 'auto' : 'none',
    }}>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute', inset: 0,
          background: 'rgba(0,0,0,0.55)',
          backdropFilter: 'blur(6px) saturate(120%)',
          WebkitBackdropFilter: 'blur(6px) saturate(120%)',
          opacity: visible ? 1 : 0,
          transition: `opacity 320ms ${EASE}`,
        }}
      />

      {/* Sheet */}
      <div
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{
        position: 'relative',
        width: '100%', maxWidth: 520,
        maxHeight: '92vh',
        background: T.surface,
        borderTopLeftRadius: ui.radius.sheet, borderTopRightRadius: ui.radius.sheet,
        boxShadow: T.cardShadowLg,
        border: `1px solid ${T.glassBorder}`,
        borderBottom: 'none',
        transform: visible
          ? `translateY(${drag}px)`
          : 'translateY(100%)',
        transition: drag === 0 ? `transform 380ms ${EASE}` : 'none',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Drag-Handle (rein visuell — gezogen wird auf der ganzen Fläche) */}
        <div
          style={{
            padding: '10px 0 6px', cursor: 'grab',
            display: 'flex', justifyContent: 'center',
          }}
        >
          <div style={{
            width: 44, height: 4, borderRadius: 999,
            background: T.glassBorder,
          }} />
        </div>

        {/* Sticky Chrome — Back links, Close rechts. Die Headline kommt aus dem
            StepHero des Inhalts (vereinheitlichter Header, kein Doppel-Titel). */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '2px 16px 10px', minHeight: 36,
        }}>
          {onBack
            ? <SheetCircleBtn T={T} onClick={onBack} label="Zurück" path="M15 18l-6-6 6-6" />
            : <span />}
          <SheetCircleBtn T={T} onClick={onClose} label="Schließen" path="M18 6L6 18M6 6l12 12" spin />
        </div>

        {/* Progress */}
        {totalSteps > 1 && <SheetProgress step={step} total={totalSteps} />}

        {/* Scrollable content */}
        <div
          ref={contentRef}
          className="hide-scrollbar"
          style={{
            flex: 1, overflowY: 'auto', overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
            // iOS Safe-Area (Home-Indicator) am unteren Rand respektieren
            padding: '4px 16px calc(env(safe-area-inset-bottom, 0px) + 24px)',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
